import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  FormHelperText,
} from '@mui/material';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';
import StudentRepository from '../services/StudentRepository.js';

export default function EmploymentStatusScreen({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  onSkip,
}) {
  const [loading, setLoading] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({
    employment_status: false,
    job_course_alignment: false,
  });

  const handleValueChange = (event) => {
    const { name, value } = event.target;

    // Clear validation errors when user makes changes
    setValidationErrors((prev) => ({
      ...prev,
      [name]: false,
    }));

    const updatedValues = {
      ...formValues,
      [name]: value,
    };

    // If employment status changes to non-employed, clear course alignment
    if (name === 'employment_status' && value !== 'employed') {
      updatedValues.job_course_alignment = null;
      setValidationErrors((prev) => ({
        ...prev,
        job_course_alignment: false,
      }));
    }

    setFormValues(updatedValues);
  };

  const validateForm = () => {
    const errors = {
      employment_status: !formValues.employment_status,
      job_course_alignment:
        formValues.employment_status === 'employed' && !formValues.job_course_alignment,
    };

    setValidationErrors(errors);
    return !errors.employment_status && !errors.job_course_alignment;
  };

  const isFormValid = () => {
    if (!formValues.employment_status) {
      return false;
    }

    if (formValues.employment_status === 'employed' && !formValues.job_course_alignment) {
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await repo.updateEmploymentStatus(
        formValues.employment_status,
        formValues.job_course_alignment
      );
      setLoading(false);
      handleScreenNext();
    } catch (error) {
      console.error(error);
      setLoading(false);
      handleErrors(
        <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
          <Typography variant="caption">
            We are unable to save your employment status. Please contact the administrator at
            webteam@msuiit.edu.ph.
          </Typography>
        </Alert>
      );
    }
  };

  const shouldShowCourseAlignment = () => {
    return formValues.employment_status === 'employed';
  };

  return (
    <React.Fragment>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          padding: '20px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Employment Status Dropdown */}
          <FormControl fullWidth error={validationErrors.employment_status} disabled={loading}>
            <InputLabel id="employment-status-label">Employment Status</InputLabel>
            <Select
              labelId="employment-status-label"
              id="employment_status"
              name="employment_status"
              value={formValues.employment_status || ''}
              label="Employment Status"
              onChange={handleValueChange}
            >
              <MenuItem value="employed">Employed</MenuItem>
              <MenuItem value="pursuing_further_studies">Pursuing Further Studies</MenuItem>
              <MenuItem value="unemployed">Unemployed</MenuItem>
            </Select>
            {validationErrors.employment_status && (
              <FormHelperText>Please select your employment status</FormHelperText>
            )}
          </FormControl>

          {/* Course Alignment Dropdown - Only shown when employed */}
          {shouldShowCourseAlignment() && (
            <FormControl fullWidth error={validationErrors.job_course_alignment} disabled={loading}>
              <InputLabel id="course-alignment-label">
                Does your current job align with your course?
              </InputLabel>
              <Select
                labelId="course-alignment-label"
                id="job_course_alignment"
                name="job_course_alignment"
                value={formValues.job_course_alignment || ''}
                label="Does your current job align with your course?"
                onChange={handleValueChange}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
              {validationErrors.job_course_alignment && (
                <FormHelperText>Please indicate if your job aligns with your course</FormHelperText>
              )}
            </FormControl>
          )}
        </Box>
      </Paper>

      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button
            variant="contained"
            size="large"
            color="error"
            onClick={() => handleScreenBack()}
            disabled={loading}
          >
            Back
          </Button>
          {onSkip && (
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={onSkip}
              sx={{ marginRight: '10px' }}
            >
              Skip to Finish
            </Button>
          )}
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={!isFormValid() || loading}
              onClick={handleNext}
            >
              Next
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                color={'success'}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

EmploymentStatusScreen.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository).isRequired,
  handleScreenNext: PropTypes.func.isRequired,
  handleScreenBack: PropTypes.func.isRequired,
  handleErrors: PropTypes.func.isRequired,
  formValues: PropTypes.shape({
    employment_status: PropTypes.string,
    job_course_alignment: PropTypes.string,
  }).isRequired,
  setFormValues: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
};
